/* ff-header-menu.js · the header's Commissions tab (Jan, 2026-10-23: "I want there to be Commissions or something as a tab, and
   when they click it they see the options"). Loaded by sections/header.liquid.

   <ff-menu-panel> (snippets/ff-header-nav.liquid, desktop): the tab opens its panel on click, Enter, Space or Arrow Down, and
   with a mouse on hover after a short pause, so passing over it does nothing. Escape, a click outside, focus moving out, and
   scrolling the panel away close it. One opened by hover also closes when the mouse leaves; a click keeps it open. Arrow keys,
   Home and End move between the pictures. While it is open the header stays in view, as Dawn's dropdown does, and Dawn's
   sticky header closes it when it hides.

   <ff-menu-accordion> (snippets/header-drawer.liquid, phones): the drawer's Commissions row folds open in place.

   The Custom Projects picture moves like "Your character, in your words" on the D&D page (assets/ff-collector.js
   armPanCycle; Jan, 2026-10-24: "i want it to be moving specifically for the custom projects image, it should be the same
   one as the moving image ... where it scrolls through the images"). Each wide picture pans across once at a steady pace,
   and near the end the next fades in over it and pans on, through every picture and round again. Both layers move at the
   same speed through the fade, so the motion never stops. It runs only while its panel or drawer row is open and the tab
   is showing, and holds where it is when they close. The other pictures are fetched the first time it runs, sized to the
   tile, and each is decoded on the hidden layer before its turn. With reduced motion it stays still. */
(() => {
  if (customElements.get('ff-menu-panel')) return;

  const OPEN_DELAY = 90;
  const CLOSE_DELAY = 220;
  /* faster and gliding (Jan, 2026-10-24: "make the images scroll faster pan, make it smooth as well"): 10 s a picture, a 1.6 s
     crossfade, drawn on the compositor (translate3d) so a slow drift never steps from pixel to pixel */
  const PAN = 10000;
  const FADE = 1600;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
  const pans = new WeakMap();

  function widthUrl(url, width) {
    if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  function makePan(first) {
    const box = first.parentNode;
    const more = (first.dataset.ffmMore || '').split('|').map((url) => url.trim()).filter(Boolean);
    if (!more.length) return null;
    let list = null;
    let layers = null;
    let cur = 0;
    let nextI = 1;
    let ready = false;
    let start = 0;
    let clock = 0;
    let last = 0;
    let raf = 0;
    let on = false;
    let fadeAt = -1;
    let fails = 0;

    function setup() {
      // the picture is twice the tile's width; 800 wide is plenty for the menu
      const width = clamp(Math.ceil((box.clientWidth * 2 * (window.devicePixelRatio || 1)) / 160) * 160, 320, 800);
      // the first picture stays as it is on screen; from here on the cycle sets each layer's src
      const shown = first.currentSrc || first.getAttribute('src');
      first.src = shown;
      first.removeAttribute('srcset');
      first.removeAttribute('sizes');
      list = [shown].concat(more.map((url) => widthUrl(url, width)));
      const back = document.createElement('img');
      back.className = 'ffm-pan';
      back.alt = '';
      back.decoding = 'async';
      back.draggable = false;
      back.setAttribute('aria-hidden', 'true');
      back.style.setProperty('--pan-o', '0');
      box.insertBefore(back, first.nextSibling);
      box.classList.add('is-cycle');
      layers = [first, back];
      document.addEventListener('visibilitychange', kick);
      load(1);
    }

    // the next picture goes on the hidden layer and is decoded there before it may fade in
    function load(i) {
      const el = layers[1 - cur];
      const src = list[i];
      nextI = i;
      ready = false;
      const done = () => {
        if (el.getAttribute('src') !== src) return;
        ready = true;
        fails = 0;
      };
      el.src = src;
      // a picture that will not load is skipped; with none loading (offline) the last one simply stays
      if (el.decode) {
        el.decode().then(done, () => {
          if (el.getAttribute('src') === src && ++fails < list.length) load((i + 1) % list.length);
        });
      } else if (el.complete) done();
      else el.onload = done;
    }

    function pos(el, t) {
      el.style.setProperty('--pan-x', `${(-50 * clamp(t / PAN, 0, 1)).toFixed(3)}%`);
    }

    function frame(now) {
      raf = 0;
      if (!on || document.hidden) {
        last = 0;
        return;
      }
      if (last) clock += Math.min(64, now - last);
      last = now;
      const a = layers[cur];
      const b = layers[1 - cur];
      pos(a, clock - start);
      if (fadeAt < 0 && ready && clock - start >= PAN - FADE) {
        fadeAt = clock;
        b.style.zIndex = '2';
        a.style.zIndex = '1';
      }
      if (fadeAt >= 0) {
        const f = clamp((clock - fadeAt) / FADE, 0, 1);
        b.style.setProperty('--pan-o', f.toFixed(3));
        pos(b, clock - fadeAt);
        if (f >= 1) {
          a.style.setProperty('--pan-o', '0');
          cur = 1 - cur;
          start = fadeAt;
          fadeAt = -1;
          load((nextI + 1) % list.length);
        }
      }
      raf = requestAnimationFrame(frame);
    }

    function kick() {
      if (!on || raf || document.hidden) return;
      last = 0;
      raf = requestAnimationFrame(frame);
    }

    return {
      play() {
        if (reducedMotion.matches) return;
        if (!list) setup();
        on = true;
        kick();
      },
      pause() {
        on = false;
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        last = 0;
      },
    };
  }

  function setPans(root, play) {
    root.querySelectorAll('img.ffm-pan[data-ffm-more]').forEach((img) => {
      if (!pans.has(img)) pans.set(img, makePan(img));
      const pan = pans.get(img);
      if (pan) play ? pan.play() : pan.pause();
    });
  }

  class FfMenuPanel extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.ffm-trigger');
      this.panel = this.querySelector('.ffm-panel');
      if (!this.trigger || !this.panel || this.ready) return;
      this.ready = true;
      this.header = this.closest('.header-wrapper');

      this.onOutside = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.onScroll = () => {
        if (Math.abs(window.scrollY - this.scrollAtOpen) > 48) this.close();
      };

      this.trigger.addEventListener('click', () => this.onClick());
      this.addEventListener('pointerenter', (event) => this.onEnter(event));
      this.addEventListener('pointerleave', (event) => this.onLeave(event));
      this.addEventListener('keydown', (event) => this.onKey(event));
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
    }

    get isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    open(how) {
      clearTimeout(this.timer);
      this.how = how;
      this.openedAt = performance.now();
      if (this.isOpen) return;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      if (this.header) this.header.preventHide = true;
      this.scrollAtOpen = window.scrollY;
      document.addEventListener('pointerdown', this.onOutside);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      setPans(this.panel, true);
    }

    close(returnFocus) {
      clearTimeout(this.timer);
      if (!this.isOpen) return;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      if (this.header) this.header.preventHide = false;
      document.removeEventListener('pointerdown', this.onOutside);
      window.removeEventListener('scroll', this.onScroll);
      setPans(this.panel, false);
      if (returnFocus) this.trigger.focus();
    }

    onClick() {
      // A click just after hover opened it keeps it open instead of shutting it under the pointer.
      if (this.isOpen && this.how === 'hover' && performance.now() - this.openedAt < 700) {
        this.how = 'click';
        return;
      }
      this.isOpen ? this.close() : this.open('click');
    }

    onEnter(event) {
      if (event.pointerType !== 'mouse' || !finePointer.matches) return;
      clearTimeout(this.timer);
      if (!this.isOpen) this.timer = setTimeout(() => this.open('hover'), OPEN_DELAY);
    }

    onLeave(event) {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.timer);
      if (this.isOpen && this.how === 'hover') this.timer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    onKey(event) {
      if (event.key === 'Escape') {
        if (!this.isOpen) return;
        event.preventDefault();
        this.close(true);
        return;
      }
      const cards = Array.from(this.panel.querySelectorAll('.ffm-card'));
      if (!cards.length) return;
      if (event.target === this.trigger) {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.open('key');
        cards[0].focus();
        return;
      }
      const at = cards.indexOf(document.activeElement);
      if (at < 0) return;
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = cards[(at + 1) % cards.length];
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = cards[(at - 1 + cards.length) % cards.length];
      else if (event.key === 'Home') next = cards[0];
      else if (event.key === 'End') next = cards[cards.length - 1];
      if (!next) return;
      event.preventDefault();
      next.focus();
    }
  }

  class FfMenuAccordion extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.ffm-acc__trigger');
      if (!this.trigger || this.ready) return;
      this.ready = true;
      this.drawer = this.closest('details');
      // Capture phase, before the button's own listeners: Dawn's drawer listens on every button inside it and would read
      // this one as a submenu's back button and close the drawer.
      this.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('.ffm-acc__trigger')) return;
          event.stopPropagation();
          const open = this.trigger.getAttribute('aria-expanded') !== 'true';
          this.trigger.setAttribute('aria-expanded', String(open));
          this.sync();
        },
        true
      );
      // the moving picture runs only while this row is open in an open drawer
      if (this.drawer) this.drawer.addEventListener('toggle', () => this.sync());
    }

    sync() {
      const open = this.trigger.getAttribute('aria-expanded') === 'true' && (!this.drawer || this.drawer.open);
      setPans(this, open);
    }
  }

  customElements.define('ff-menu-panel', FfMenuPanel);
  customElements.define('ff-menu-accordion', FfMenuAccordion);
})();
